/**
 * CYP ASD Semi-Structured Interview — Main Page
 *
 * Entry-point for the Developmental Autism Assessment (Child/Adolescent Version)
 * based on DSM-5 criteria.
 *
 * The SSI guides clinicians through each DSM-5 ASD criterion (A1–A3, B1–B4)
 * using structured probing questions and behaviour indicator checklists, then
 * assesses functional impairment across six life domains.
 *
 * Architecture mirrors the Adult ADHD SSI (sidebar + main panel wizard) but
 * adapted for ASD-specific content:
 *   • Left sidebar  — CypAsdSSISideNavbar (criterion list with group headers)
 *   • Right panel   — CypAsdCriterionPanel (when a criterion is selected) or
 *                      CypAsdFunctionalImpairmentPanel (when FI is selected)
 *   • State is fully local (no Firestore writes) for the initial mockup.
 *   • A fixed floating footer lets clinicians toggle visual preferences
 *     (e.g. evidence display mode) without leaving the current view.
 *
 * Clinical context (scoring notes, criterion descriptions, disclaimers) is
 * surfaced via InfoTooltips on each criterion title rather than top-level banners,
 * keeping the interface clean and focused.
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ExclamationIcon } from '@heroicons/react/solid';
import { useVideoStream } from '../../../context/VideoStreamContext';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import CypAsdSSISideNavbar from './components/CypAsdSSISideNavbar';
import CypAsdCriterionPanel from './components/CypAsdCriterionPanel';
import CypAsdFunctionalImpairmentPanel from './components/CypAsdFunctionalImpairmentPanel';
import SSIDisplayPreferencesFooter from './components/SSIDisplayPreferencesFooter';
// SSIFunctionalImpairmentFooter import kept for reference — FI is now inline in criterion panels
// import SSIFunctionalImpairmentFooter from './components/SSIFunctionalImpairmentFooter';
import SSI_CRITERIA from './config/cypAsdSSIConfig';
import {
  EMPTY_SSI_ALL_DATA,
  isDemoMode, setDemoMode,
} from '../seedData/cypAsdSeedData';

export default function CypAsdSSI() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const videoStream = useVideoStream();

  /**
   * Demo mode toggle — when ON the page loads with realistic dummy data
   * for demonstration purposes; when OFF the page starts empty so the
   * user can test the UI/UX by entering their own data.
   */
  const [demoMode, setDemoModeLocal] = useState(() => isDemoMode(patientId));

  const handleDemoToggle = useCallback(() => {
    const next = !demoMode;
    setDemoMode(patientId, next);
    setDemoModeLocal(next);
    window.location.reload();
  }, [demoMode, patientId]);

  // Opt this page into the floating video panel while it's mounted
  useEffect(() => {
    videoStream?.setVideoEnabled(true);
    return () => videoStream?.setVideoEnabled(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Which criterion or section is currently displayed in the right panel.
   * Values: 'A1', 'A2', ..., 'B4', or 'FI' for Functional Impairment.
   * Defaults to the first available criterion.
   */
  const firstAvailable = SSI_CRITERIA.find(c => c.available);
  const [activeItemId, setActiveItemId] = useState(firstAvailable?.id || 'A1');

  /**
   * Session-storage key for this patient's SSI data.  Keeps symptom evidence,
   * domain selections, and notes alive across navigations between the SSI and
   * Review pages without requiring Firestore writes in this mockup phase.
   */
  const storageKey = `cyp-asd-ssi-${patientId}`;

  /**
   * All form data for all criteria and FI, keyed by section ID then field ID.
   * Seeded from localStorage so data survives navigation to/from Review.
   */
  const [allData, setAllData] = useState(() => {
    try {
      const cached = localStorage.getItem(storageKey);
      if (cached) return JSON.parse(cached);
    } catch { /* ignore */ }
    return EMPTY_SSI_ALL_DATA;
  });

  /** Persist allData to localStorage whenever it changes. */
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(allData));
    } catch { /* quota exceeded — ignore in mockup */ }
  }, [storageKey, allData]);

  /**
   * Visual display preferences — controlled by the floating footer panel.
   * These let the clinician toggle between UI variants in real-time so they
   * can pick the layout they find most comfortable during an assessment.
   */
  const [displayPreferences, setDisplayPreferences] = useState({
    evidenceMode: 'buttons', // 'dropdown' | 'buttons'
    sidebarMode: 'compact',  // 'compact' | 'expanded'
    navMode: 'bottom',       // 'bottom' | 'both'
  });

  /**
   * Validation dialog state — shown when the clinician tries to navigate
   * to Review before all symptoms have both evidence decisions recorded.
   */
  const [showValidationDialog, setShowValidationDialog] = useState(false);

  /**
   * Identifies which criteria are still missing evidence decisions.
   * Each entry has the criterion id, shortTitle, and which stages are missing.
   */
  const incompleteSymptoms = useMemo(() => {
    return SSI_CRITERIA.filter(c => c.available).reduce((acc, c) => {
      const d = allData[c.id] || {};
      const missingEarly = !d.evidence_early;
      const missingCurrent = !d.evidence_current;
      if (missingEarly || missingCurrent) {
        acc.push({
          id: c.id,
          shortTitle: c.shortTitle,
          missingEarly,
          missingCurrent,
        });
      }
      return acc;
    }, []);
  }, [allData]);

  /** Navigate directly to the review page, bypassing validation. */
  const proceedToReview = useCallback(() => {
    setShowValidationDialog(false);
    navigate(
      `/patient/${patientId}/proforma/cyp-asd/semi-structured-interview/review`,
      { state: { allData } }
    );
  }, [navigate, patientId, allData]);

  /**
   * Navigate to a different criterion, the FI panel, or the Review page.
   * 'REVIEW' is a special sentinel — before navigating, we validate that
   * every available symptom has both early and current evidence decisions.
   * If not, a dialog gives the clinician the option to go back and
   * complete the missing decisions, or proceed to Review anyway.
   */
  const handleNavigate = useCallback((itemId) => {
    if (itemId === 'REVIEW') {
      if (incompleteSymptoms.length > 0) {
        setShowValidationDialog(true);
      } else {
        proceedToReview();
      }
      return;
    }
    setActiveItemId(itemId);
    const mainContentArea = document.querySelector('.flex-1.overflow-y-auto');
    if (mainContentArea) {
      mainContentArea.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [incompleteSymptoms, proceedToReview]);

  /**
   * Update a single field within the currently active section.
   * Merges into existing data for that section.
   */
  const handleFieldChange = useCallback((fieldId, value) => {
    setAllData(prev => ({
      ...prev,
      [activeItemId]: {
        ...(prev[activeItemId] || {}),
        [fieldId]: value,
      },
    }));
  }, [activeItemId]);

  const handlePreferenceChange = useCallback((key, value) => {
    setDisplayPreferences(prev => ({ ...prev, [key]: value }));
  }, []);

  const activeCriterion = SSI_CRITERIA.find(c => c.id === activeItemId);
  const isFI = activeItemId === 'FI';

  // Build the active criterion's short label for the breadcrumb trail
  const activeLabel = isFI
    ? 'Functional Impairment'
    : activeCriterion
      ? `${activeCriterion.id}. ${activeCriterion.shortTitle}`
      : null;

  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}`],
    ['CYP ASD Assessment'],
    ['Diagnostic Developmental History', `/patient/${patientId}/proforma/cyp-asd/history`],
    ['Semi-Structured Interview'],
    ...(activeLabel ? [[activeLabel]] : []),
  ];

  return (
    <Layout activePage="dashboard">
      <div className="flex items-center">
        <Breadcrumb tupleList={breadcrumbTuples} />
        <div className="flex-shrink-0 pr-8 flex items-center gap-2 ml-auto">
          <span className="text-xs text-gray-400 font-medium mr-1">Demo Data:</span>
          <button
            onClick={handleDemoToggle}
            className={`
              relative w-9 h-5 rounded-full transition-colors duration-200 flex-shrink-0
              ${demoMode ? 'bg-amber-500' : 'bg-gray-300'}
            `}
            title={demoMode ? 'Turn off demo data — start with a blank page' : 'Turn on demo data — pre-fill with example content'}
          >
            <span
              className={`
                absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm
                transition-transform duration-200
                ${demoMode ? 'translate-x-4' : 'translate-x-0'}
              `}
            />
          </button>
        </div>
      </div>

      <div className="flex h-[calc(100vh-8rem)]">
        {/* Left sidebar — criterion navigation */}
        <CypAsdSSISideNavbar
          criteria={SSI_CRITERIA}
          activeItemId={activeItemId}
          onSelectItem={handleNavigate}
          criterionData={allData}
          sidebarMode={displayPreferences.sidebarMode}
        />

        {/* Right panel — active criterion form or FI panel */}
        <div className="flex-1 min-w-0 overflow-hidden">
          {isFI ? (
            <CypAsdFunctionalImpairmentPanel
              fiData={allData['FI'] || {}}
              onFieldChange={handleFieldChange}
              onNavigate={handleNavigate}
              criteria={SSI_CRITERIA}
            />
          ) : activeCriterion ? (
            <CypAsdCriterionPanel
              criterion={activeCriterion}
              criterionData={allData[activeCriterion.id] || {}}
              onFieldChange={handleFieldChange}
              onNavigate={handleNavigate}
              criteria={SSI_CRITERIA}
              displayPreferences={displayPreferences}
            />
          ) : (
            <div className="flex items-center justify-center h-96 text-gray-500 text-sm">
              Select a criterion from the sidebar
            </div>
          )}
        </div>
      </div>

      {/* Fixed floating header — visual customisation panel */}
      <SSIDisplayPreferencesFooter
        preferences={displayPreferences}
        onPreferenceChange={handlePreferenceChange}
      />

      {/* ---- Validation dialog — shown when Review is requested with incomplete decisions ---- */}
      {showValidationDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowValidationDialog(false)}
          />

          {/* Dialog card — no max-h / overflow so it grows with content */}
          <div className="relative bg-white rounded-xl shadow-2xl p-6 w-[560px] max-w-[90vw] text-left">
            {/* Close button */}
            <button
              onClick={() => setShowValidationDialog(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-xl leading-none"
              aria-label="Close"
            >
              ×
            </button>

            {/* Header */}
            <div className="flex items-center gap-2 mb-4">
              <ExclamationIcon className="w-6 h-6 text-amber-500 flex-shrink-0" />
              <h2 className="text-lg font-bold text-gray-900">
                Evidence Decisions Required
              </h2>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              The following DSM criteria are missing evidence decisions for one or
              both life stages.
            </p>

            {/* Bullet list of incomplete symptoms */}
            <ul className="bg-red-50 rounded-lg p-3 pl-5 mb-4 list-disc list-inside space-y-1">
              {incompleteSymptoms.map((s) => (
                <li key={s.id} className="text-sm text-red-800 font-medium">
                  {s.id}. {s.shortTitle}
                </li>
              ))}
            </ul>

            {/* Warning note */}
            <p className="text-sm italic text-amber-700 bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-5">
              <strong>Note:</strong> If you proceed without completing these, the
              criteria summary and auto-prefill on the Review page may be incomplete.
            </p>

            {/* Action buttons */}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  const firstMissing = incompleteSymptoms[0];
                  if (firstMissing) {
                    setActiveItemId(firstMissing.id);
                    setShowValidationDialog(false);
                  }
                }}
                className="flex items-center py-2 px-4 rounded-md shadow-md bg-primary text-white hover:bg-primary/90 transition-all duration-200 font-semibold text-sm"
              >
                Go back to DSM Criteria
              </button>
              <button
                onClick={proceedToReview}
                className="flex items-center py-2 px-4 rounded-md shadow-md text-gray-900 border border-gray-300 hover:bg-gray-50 transition-all duration-200 font-semibold text-sm"
              >
                Proceed Anyway
              </button>
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
}
